const DRAFT_PREFIX = "wilearn:draft:";
const SENSITIVE_FIELD_PATTERN = /(password|passcode|otp|token|secret|card|cvv|cvc|expiry|payment|bank|account|ifsc|pan|upi|auth)/i;

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const isBrowserStorageAvailable = () => (
  typeof window !== "undefined" && Boolean(window.localStorage)
);

const getDraftOwnerId = () => {
  if (!isBrowserStorageAvailable()) return "anonymous";
  const id = localStorage.getItem("id");
  const email = localStorage.getItem("email");
  const role = localStorage.getItem("role");
  return [role || "guest", id || email || "anonymous"].join(":");
};

const normalizeKeyPart = (value) => encodeURIComponent(String(value || "default"));

export const getDraftStorageKey = (formId, scope = "default") => (
  `${DRAFT_PREFIX}${normalizeKeyPart(getDraftOwnerId())}:${normalizeKeyPart(formId)}:${normalizeKeyPart(scope)}`
);

const shouldExcludeKey = (key, excludedFields = [], includedFields = []) => {
  const keyText = Array.isArray(key) ? key.join(".") : String(key);
  if (includedFields.includes(keyText)) return false;
  return excludedFields.includes(keyText) || SENSITIVE_FIELD_PATTERN.test(keyText);
};

export const sanitizeDraftValue = (value, excludedFields = [], path = [], includedFields = []) => {
  if (value == null) return value;
  if (typeof File !== "undefined" && value instanceof File) return undefined;
  if (typeof Blob !== "undefined" && value instanceof Blob) return undefined;
  if (Array.isArray(value)) {
    return value
      .map((item, index) => sanitizeDraftValue(item, excludedFields, [...path, index], includedFields))
      .filter((item) => item !== undefined);
  }
  if (typeof value === "object") {
    return Object.entries(value).reduce((draft, [key, nestedValue]) => {
      const nextPath = [...path, key];
      if (shouldExcludeKey(nextPath, excludedFields, includedFields)) return draft;
      const sanitized = sanitizeDraftValue(nestedValue, excludedFields, nextPath, includedFields);
      if (sanitized !== undefined) draft[key] = sanitized;
      return draft;
    }, {});
  }
  return value;
};

export const readDraft = (formId, scope = "default") => {
  if (!isBrowserStorageAvailable()) return null;
  const item = safeParse(localStorage.getItem(getDraftStorageKey(formId, scope)));
  return item?.values ?? null;
};

export const saveDraft = (formId, values, options = {}) => {
  if (!isBrowserStorageAvailable()) return;
  const { scope = "default", excludedFields = [], includedFields = [] } = options;
  const sanitized = sanitizeDraftValue(values, excludedFields, [], includedFields);
  localStorage.setItem(
    getDraftStorageKey(formId, scope),
    JSON.stringify({ values: sanitized, updatedAt: new Date().toISOString() })
  );
};

export const clearDraft = (formId, scope = "default") => {
  if (!isBrowserStorageAvailable()) return;
  localStorage.removeItem(getDraftStorageKey(formId, scope));
};

export const clearAllDrafts = () => {
  if (!isBrowserStorageAvailable()) return;
  Object.keys(localStorage)
    .filter((key) => key.startsWith(DRAFT_PREFIX))
    .forEach((key) => localStorage.removeItem(key));
};
